import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  ModeratedTaskFormat,
  Participation,
  SessionFormat,
  StudyType,
  useMe,
  useParticipant,
  useTaskMeeting,
} from '@app/api';
import { CardHomeBase, StyleCardHome } from './CardHomeBase';
import { DisplayDate } from './DisplayDate';
import { IconsMeetingList } from './IconsMeetingList';
import { useTranslation } from 'react-i18next';
import { Button, useParticipantUnreadMessageByParticipation } from '@app/core';
import classnames from 'classnames';
import moment from 'moment';
import { LinkToMeetingButton } from '../../components/LinkToMeetingButton';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonContainer: {
    display: 'flex',
    gap: theme.marginBase,
    marginTop: theme.marginBase * 3,
    flexWrap: 'wrap',
  },
  btnText: {
    ...theme.fonts.label,
  },
  icons: {
    fontSize: theme.iconSize.large,
    paddingLeft: theme.marginBase,
    alignSelf: 'center',
    fontWeight: 500,
  },
  descriptionModal: {
    ...theme.fonts.caption,
  },
  noLinkContainer: {
    backgroundColor: theme.colors.white,
    marginTop: theme.marginBase,
    borderRadius: theme.borderRadius.std,
    maxHeight: 100,
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  easeIn: {
    transition: 'max-height .2s step-end',
  },
  easeOut: {
    transition: 'max-height .2s step-start',
  },
  noLinkDescription: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    padding: theme.marginBase * 2,
  },
  hideNoLink: {
    maxHeight: 0,
    transition: 'max-height .2s ease-out',
    flex: 1,
    justifyContent: 'center',
  },
  noLinkBtn: {
    opacity: 0.5,
    justifyContent: 'center',
    flexGrow: 1,
  },
  button: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  address: {
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  participation: Participation;
  className?: string;
  variant?: 'plain' | 'transparent';
}

export const BookedParticipationCard = ({
  className,
  participation,
  variant = 'plain',
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: participant } = useParticipant(participation.participantId);
  const { data: me } = useMe();
  const { data: meeting } = useTaskMeeting(participation.taskId);
  const timezone = participant?.timezone || me?.timezone || moment.tz.guess();

  const isUnModerated =
    participation.session?.studyType === StudyType.UNMODERATED;

  const numberOfNewMessages: number =
    useParticipantUnreadMessageByParticipation(
      participant?.id,
      participation.id,
    );

  const meetingIsInLessThanTwoHours = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(currentDate.getHours() + 2);
    return (
      participation.appointment?.date &&
      new Date(participation.appointment.date) <= currentDate
    );
  }, [participation]);

  const dayToMeeting = useMemo(() => {
    if (!participation.appointment) return -1;
    return moment(participation.appointment.date)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
  }, [participation]);

  const linkToMeetingExist = useMemo(() => {
    if (meeting?.format === ModeratedTaskFormat.VIDEO_CALL) {
      return meeting.videoCallLink;
    }
    return undefined;
  }, [meeting]);

  const focus = variant === 'plain';
  const isVideoCall =
    participation.session?.format === SessionFormat.VIDEO_CALL;

  const date = useMemo(() => {
    if (isUnModerated && participation.automaticallyRejectAt) {
      return new Date(participation.automaticallyRejectAt);
    }
    if (!isUnModerated && participation.appointment) {
      return new Date(participation.appointment.date);
    }
    return undefined;
  }, [participation, isUnModerated]);

  if ((!participation?.appointment || !participant) && !isUnModerated)
    return null;

  return (
    <CardHomeBase
      style={focus ? StyleCardHome.GREEN : StyleCardHome.WHITE}
      icon={focus ? 'ri-calendar-event-line' : undefined}
      title={
        !participation?.appointment
          ? t('home.nextMeeting.noMeeting')
          : focus
          ? t('home.nextMeeting.title')
          : t('home.nextMeeting.dayToMeeting', {
              count: dayToMeeting,
            })
      }
      className={className}
    >
      {date && <DisplayDate date={date} timeZone={timezone} focus={focus} />}
      <p>
        {participation.session?.title && <b>{participation.session.title}</b>}
      </p>
      {meeting?.address && (
        <p className={classes.address}>
          <b>{t('home.nextMeeting.address')} </b>
          {meeting.address}
        </p>
      )}
      <IconsMeetingList
        session={participation.session}
        focus={focus}
        participation={participation}
        numberOfNewMessages={numberOfNewMessages}
      />
      <div className={classes.buttonContainer}>
        <Button
          color="green"
          variant={focus ? 'plain' : 'muted'}
          small={true}
          text={
            isUnModerated
              ? t('home.profileAccepted.startStudy')
              : t('home.nextMeeting.manageAppointment')
          }
          className={classes.button}
          to={`/p/${participation.id}`}
          state={{ allowBack: true }}
        />
        {meetingIsInLessThanTwoHours && isVideoCall && (
          <LinkToMeetingButton
            participation={participation}
            className={classes.button}
            color="ink"
            variant="muted"
          ></LinkToMeetingButton>
        )}
      </div>
      {meetingIsInLessThanTwoHours && !linkToMeetingExist && isVideoCall && (
        <div className={classnames(classes.noLinkContainer)}>
          <h5 className={classes.noLinkDescription}>
            {t('home.modal.noLink.description')}
          </h5>
        </div>
      )}
    </CardHomeBase>
  );
};
